import { apiClient } from '../api/client';

export { apiClient };

// Types
export interface Author {
  id: number;
  name: string;
  created_at: string;
}

export interface Source {
  id: number;
  type: 'book' | 'article';
  title: string;
  isbn?: string;
  doi?: string;
  issn?: string;
  publisher?: string;
  year?: number;
  authors: { id: number; name: string }[];
  created_at: string;
}

export interface Tag {
  id: number;
  name: string;
  normalized_name: string;
  created_at: string;
}

export interface TagSearchResult {
  tag: Tag;
  similarity: number;
}

export interface QuoteImage {
  id: number;
  quote_id: number;
  image_path: string;
  thumbnail_path: string;
  page_number?: number;
  display_order: number;
  created_at: string;
}

export interface Quote {
  id: number;
  source_id: number;
  text_content: string | null;
  text_status: 'ai_generated' | 'manual_verified';
  images: QuoteImage[];
  tags: Tag[];
  source?: Source;
  created_at: string;
}

// API Functions
export const getAuthors = () => apiClient.get<Author[]>('/authors');
export const createAuthor = (data: { name: string }) => apiClient.post<Author>('/authors', data);

export const getSources = () => apiClient.get<Source[]>('/sources');
export const createSource = (data: Partial<Source>) => apiClient.post<Source>('/sources', data);

export const getQuotes = (params?: { source_id?: number; text_status?: string }) =>
  apiClient.get<Quote[]>('/quotes', { params });

export const getQuote = (id: number) => apiClient.get<Quote>(`/quotes/${id}`);

export const createQuote = (data: FormData): Promise<{ data: Quote }> => {
  const token = localStorage.getItem('token');
  return fetch('/api/quotes', {
    method: 'POST',
    body: data,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  }).then(async (res) => {
    const json = await res.json();
    if (!res.ok) throw { response: { data: json, status: res.status } };
    return { data: json as Quote };
  });
};

export const updateQuote = (id: number, data: Partial<Quote>) =>
  apiClient.put<Quote>(`/quotes/${id}`, data);

export const deleteQuote = (id: number) => apiClient.delete(`/quotes/${id}`);

export const verifyQuoteText = (id: number) =>
  apiClient.post<Quote>(`/quotes/${id}/verify-text`);

export const getTags = () => apiClient.get<Tag[]>('/tags');
export const searchTags = (q: string) => apiClient.get<TagSearchResult[]>(`/tags/search?q=${encodeURIComponent(q)}`);

export const createTag = (data: { name: string }) => apiClient.post<Tag>('/tags', data);
